import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { X, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

const STORAGE_KEY = "aurora_demo_tour_v1";

const STEPS = [
  {
    title: "Bienvenido al demo de Aurora",
    description: "Estás en el panel clínico. Aquí verás el resumen de tus pacientes, alertas geriátricas y accesos rápidos a todas las funciones.",
    action: null,
    actionLabel: null,
  },
  {
    title: "Pacientes con historial completo",
    description: "Aurora tiene 6 pacientes de ejemplo listos. Cada uno tiene historia clínica, diagnósticos, medicamentos y alertas clínicas activas.",
    action: "/pacientes",
    actionLabel: "Ver pacientes →",
  },
  {
    title: "Consulta geriátrica completa",
    description: "El flujo de consulta incluye historia clínica, examen físico y plan terapéutico — todo en un solo expediente.",
    action: "/nueva-consulta",
    actionLabel: "Ver flujo de consulta →",
  },
  {
    title: "Consulta geriátrica estructurada",
    description: "El flujo de consulta reúne historia clínica, examen físico y plan terapéutico en un solo expediente, con continuidad entre visitas.",
    action: "/nueva-consulta",
    actionLabel: "Ver flujo de consulta →",
  },
  {
    title: "Informes PDF automáticos",
    description: "Aurora genera informes profesionales con un clic — informe clínico, documento para familia y plan terapéutico. Todos con el logo de tu clínica.",
    action: null,
    actionLabel: null,
  },
  {
    title: "¿Listo para usar Aurora?",
    description: "Prueba Aurora gratis por 14 días con todos tus propios pacientes. Sin tarjeta de crédito. Acceso inmediato.",
    action: "/register",
    actionLabel: "Crear mi cuenta gratis →",
    isCTA: true,
  },
];

export default function DemoTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (user?.email !== "demo@aurorageriatrica.com") return;
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setVisible(false);
  };

  const handleAction = () => {
    const current = STEPS[step];
    if (current.isCTA) {
      handleClose();
      navigate("/register");
      return;
    }
    if (current.action) {
      navigate(current.action);
    }
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      handleClose();
    }
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else handleClose();
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  if (!visible) return null;

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center pb-8 px-4 pointer-events-none">
      <div className="pointer-events-auto w-full max-w-md rounded-2xl border border-emerald-200 bg-white shadow-2xl shadow-emerald-600/10">
        
        <div className="bg-emerald-600 rounded-t-2xl px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-white" />
            <p className="text-xs font-semibold text-white uppercase tracking-wider">
              Tour Aurora — Paso {step + 1} de {STEPS.length}
            </p>
          </div>
          <button onClick={handleClose} className="text-emerald-200 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4">
          <h3 className="text-base font-bold text-slate-900 mb-2">{current.title}</h3>
          <p className="text-sm text-slate-600 leading-relaxed">{current.description}</p>
        </div>

        <div className="flex items-center gap-2 px-5 pb-4">
          <div className="flex gap-1 flex-1">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  i <= step ? "bg-emerald-600" : "bg-slate-200"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 px-5 pb-5">
          {step > 0 && (
            <button
              onClick={handleBack}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Anterior
            </button>
          )}
          <div className="flex-1" />
          {current.actionLabel && (
            <button
              onClick={handleAction}
              className={`flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-semibold text-white transition-all ${
                current.isCTA
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-sky-600 hover:bg-sky-700"
              }`}
            >
              {current.actionLabel}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            onClick={handleNext}
            className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900 border border-slate-200 rounded-xl px-3 py-2"
          >
            {isLast ? "Finalizar" : "Siguiente"}
            {!isLast && <ArrowRight className="h-3.5 w-3.5" />}
          </button>
        </div>

      </div>
    </div>
  );
}
